import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, TextField,
  MenuItem, CircularProgress, Alert, ToggleButton, ToggleButtonGroup,
  InputAdornment, Tabs, Tab, useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VerticalAlignBottom as ScrollIcon,
  Terminal as TerminalIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Close as ClearIcon,
  Download as DownloadIcon,
  WrapText as WrapIcon,
  DeleteSweep as ClearLogIcon,
  Dns as NodeIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import { parseServerTs } from '../../timezone';

const BASE = '/api/operator/container-logs';
const POLL_MS = 3000;
const MAX_LINES = 3000;
const ALL_NODES = '__all__';

// Classify a log line so the viewer can colour it by severity.
const levelOf = (msg = '') => {
  if (/\b(ERROR|ERR|FATAL|PANIC|EXCEPTION|SEVERE)\b/i.test(msg)) return 'error';
  if (/\b(WARN|WARNING)\b/i.test(msg)) return 'warn';
  if (/\b(DEBUG|TRACE)\b/i.test(msg)) return 'debug';
  return 'info';
};
const LEVEL_COLOR = { error: '#f87171', warn: '#fbbf24', info: '#d7dee8', debug: '#7c8698' };

// Split a line around a case-insensitive query so matches can be <mark>-ed.
const highlight = (text, q) => {
  if (!q) return text;
  const parts = [];
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  let i = 0;
  let idx = lower.indexOf(needle);
  if (idx === -1) return text;
  while (idx !== -1) {
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark key={idx} style={{ background: '#facc15', color: '#111', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    i = idx + q.length;
    idx = lower.indexOf(needle, i);
  }
  if (i < text.length) parts.push(text.slice(i));
  return parts;
};

const OperatorContainerLogsPage = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const notification = useNotification();

  const [containers, setContainers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [lines, setLines] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [live, setLive] = useState(true);
  const [tail, setTail] = useState(500);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Navigation / filtering
  const [activeNode, setActiveNode] = useState(ALL_NODES);
  const [containerQuery, setContainerQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('all');   // all | running | exited

  // Log viewer controls
  const [logQuery, setLogQuery] = useState('');
  const [streamFilter, setStreamFilter] = useState('all');  // all | stdout | stderr
  const [wrap, setWrap] = useState(true);

  const lastTsRef = useRef(null);      // ISO ts of the last appended line (dedupe cursor)
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);

  const selected = containers.find((c) => c.id === selectedId) || null;

  // ── Derived: nodes with per-node stats ────────────────────────────────
  const nodes = useMemo(() => {
    const map = new Map();
    for (const c of containers) {
      const n = c.node || 'node';
      if (!map.has(n)) map.set(n, { node: n, total: 0, running: 0 });
      const s = map.get(n);
      s.total += 1;
      if (c.state === 'running') s.running += 1;
    }
    return Array.from(map.values()).sort((a, b) => a.node.localeCompare(b.node));
  }, [containers]);

  const clusterStats = useMemo(() => ({
    nodes: nodes.length,
    total: containers.length,
    running: containers.filter((c) => c.state === 'running').length,
  }), [nodes, containers]);

  // ── Derived: the visible container list (node → search → state) ───────
  const visibleContainers = useMemo(() => {
    const q = containerQuery.trim().toLowerCase();
    return containers.filter((c) => {
      if (activeNode !== ALL_NODES && (c.node || 'node') !== activeNode) return false;
      if (stateFilter === 'running' && c.state !== 'running') return false;
      if (stateFilter === 'exited' && c.state === 'running') return false;
      if (q && !(`${c.name} ${c.image}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [containers, activeNode, containerQuery, stateFilter]);

  // ── Container list fetch ──────────────────────────────────────────────
  const loadContainers = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`${BASE}/containers`);
      setContainers(Array.isArray(data.containers) ? data.containers : []);
      setUnavailable(false);
    } catch (e) {
      if (e?.response?.status === 503) {
        setUnavailable(true);
      } else {
        setError(e?.response?.data?.error || e.message || 'Failed to list containers');
      }
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadContainers(); }, [loadContainers]);

  // Keep a valid selection: if the selected container leaves the visible
  // set (node switch, filter, or it disappeared), select the first visible.
  useEffect(() => {
    if (visibleContainers.length === 0) return;
    if (!visibleContainers.some((c) => c.id === selectedId)) {
      setSelectedId(visibleContainers[0].id);
    }
  }, [visibleContainers, selectedId]);

  // ── Log fetch (initial when container changes, then incremental) ──────
  const fetchLogs = useCallback(async (id, incremental) => {
    if (!id) return;
    if (!incremental) setLoadingLogs(true);
    try {
      const c = containers.find((x) => x.id === id);
      const params = { tail };
      if (c?.node) params.node = c.node; // route to the node the container runs on
      if (incremental && lastTsRef.current) {
        const sinceMs = parseServerTs(lastTsRef.current);
        if (Number.isFinite(sinceMs)) params.since = Math.floor(sinceMs / 1000);
      }
      const { data } = await apiClient.get(`${BASE}/${encodeURIComponent(id)}/logs`, { params });
      const incoming = Array.isArray(data.lines) ? data.lines : [];
      const cursor = lastTsRef.current;
      const fresh = incremental && cursor ? incoming.filter((l) => l.ts && l.ts > cursor) : incoming;
      if (fresh.length) {
        const last = fresh[fresh.length - 1];
        if (last.ts) lastTsRef.current = last.ts;
        setLines((prev) => {
          const next = incremental ? [...prev, ...fresh] : fresh;
          return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
        });
      } else if (!incremental) {
        setLines([]);
      }
      setUpdatedAt(Date.now());
      setError(null);
    } catch (e) {
      if (!incremental) setError(e?.response?.data?.error || e.message || 'Failed to load logs');
    } finally {
      if (!incremental) setLoadingLogs(false);
    }
  }, [tail, containers]);

  // Reset + initial load when the selected container (or tail size) changes.
  useEffect(() => {
    if (!selectedId) return;
    lastTsRef.current = null;
    setLines([]);
    fetchLogs(selectedId, false);
  }, [selectedId, tail, fetchLogs]);

  // Live-tail polling.
  useEffect(() => {
    if (!live || !selectedId || unavailable) return undefined;
    const t = setInterval(() => fetchLogs(selectedId, true), POLL_MS);
    return () => clearInterval(t);
  }, [live, selectedId, unavailable, fetchLogs]);

  // ── Visible log lines (stream + text filter) ──────────────────────────
  const visibleLines = useMemo(() => {
    const q = logQuery.trim().toLowerCase();
    return lines.filter((l) => {
      if (streamFilter !== 'all' && (l.stream || 'stdout') !== streamFilter) return false;
      if (q && !(l.message || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [lines, logQuery, streamFilter]);

  // Auto-scroll to bottom on new lines while pinned to the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [visibleLines]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (el) { el.scrollTop = el.scrollHeight; atBottomRef.current = true; }
  };

  const copyAll = () => {
    const text = visibleLines.map((l) => `${l.ts} ${l.message}`).join('\n');
    navigator.clipboard?.writeText(text).then(() => notification.success('Logs copied'));
  };

  const downloadLogs = () => {
    if (!selected) return;
    const text = visibleLines.map((l) => `${l.ts} ${l.stream === 'stderr' ? '[stderr] ' : ''}${l.message}`).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selected.name}_${selected.node || 'node'}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stateColor = (state) =>
    state === 'running' ? 'success' : state === 'exited' ? 'error' : 'default';

  if (unavailable) {
    return (
      <Box sx={{ p: 3 }}>
        <PageTitle stats={null} onRefresh={loadContainers} />
        <Alert severity="warning" sx={{ mt: 2 }}>
          The Docker socket isn't available to this backend. Mount
          <code style={{ margin: '0 6px' }}>/var/run/docker.sock</code>
          (read-only) into the <code>verops-backend</code> containers and redeploy to enable container logs.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageTitle stats={clusterStats} onRefresh={loadContainers} loading={loadingList} />

      {/* Node selector — the cross-node switcher */}
      <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2, px: 1 }}>
        <Tabs
          value={activeNode}
          onChange={(_, v) => setActiveNode(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none' } }}
        >
          <Tab
            value={ALL_NODES}
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <NodeIcon fontSize="small" />
                <span style={{ fontWeight: 700 }}>All nodes</span>
                <Chip size="small" label={clusterStats.total} sx={{ height: 18, fontSize: '0.66rem' }} />
              </Stack>
            }
          />
          {nodes.map((n) => (
            <Tab
              key={n.node}
              value={n.node}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box component="span" sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: n.running === n.total ? '#22c55e' : n.running === 0 ? '#ef4444' : '#f59e0b',
                  }} />
                  <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{n.node}</span>
                  <Chip size="small" label={`${n.running}/${n.total}`} sx={{ height: 18, fontSize: '0.66rem' }} />
                </Stack>
              }
            />
          ))}
        </Tabs>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mt: 2, flex: 1, minHeight: 0 }}>
        {/* Container list */}
        <Paper variant="outlined" sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
          {/* List toolbar */}
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small" fullWidth placeholder="Filter containers…"
              value={containerQuery}
              onChange={(e) => setContainerQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: containerQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setContainerQuery('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
                sx: { fontSize: 13 },
              }}
            />
            <ToggleButtonGroup
              size="small" exclusive value={stateFilter}
              onChange={(_, v) => v && setStateFilter(v)}
              sx={{ mt: 1, '& .MuiToggleButton-root': { textTransform: 'none', px: 1.25, py: 0.25, fontSize: 12 } }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="running">Running</ToggleButton>
              <ToggleButton value="exited">Stopped</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loadingList ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
            ) : visibleContainers.length === 0 ? (
              <Typography sx={{ p: 2, color: 'text.secondary', fontSize: 13 }}>
                No containers match.
              </Typography>
            ) : (
              visibleContainers.map((c) => {
                const sel = c.id === selectedId;
                return (
                  <Box
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(c.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(c.id); } }}
                    sx={{
                      px: 1.5, py: 1, cursor: 'pointer', borderBottom: 1, borderColor: 'divider',
                      bgcolor: sel ? (dark ? 'rgba(59,130,246,0.14)' : 'rgba(59,130,246,0.08)') : 'transparent',
                      borderLeft: sel ? '3px solid #3b82f6' : '3px solid transparent',
                      '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.name}
                      </Typography>
                      <Chip size="small" label={c.state} color={stateColor(c.state)} sx={{ height: 18, fontSize: '0.62rem' }} />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
                      {activeNode === ALL_NODES && (
                        <Chip
                          size="small" icon={<NodeIcon sx={{ fontSize: 12 }} />} label={c.node || 'node'}
                          variant="outlined"
                          sx={{ height: 17, fontSize: '0.6rem', fontFamily: 'monospace', '& .MuiChip-icon': { ml: '4px' } }}
                        />
                      )}
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.image}>
                        {c.image}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>

        {/* Log viewer */}
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
          {/* Viewer header — container identity + node */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {selected ? selected.name : '—'}
            </Typography>
            {selected && (
              <Chip size="small" icon={<NodeIcon sx={{ fontSize: 13 }} />} label={selected.node || 'node'}
                sx={{ height: 20, fontSize: '0.66rem', fontFamily: 'monospace' }} />
            )}
            {selected && <Chip size="small" label={selected.state} color={stateColor(selected.state)} sx={{ height: 20, fontSize: '0.66rem' }} />}
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={selected?.status}>
              {selected?.status || ''}
            </Typography>
          </Stack>

          {/* Viewer toolbar — in-log search + controls */}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
            <TextField
              size="small" placeholder="Search in logs…"
              value={logQuery}
              onChange={(e) => setLogQuery(e.target.value)}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: logQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setLogQuery('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
                sx: { fontSize: 12.5, fontFamily: 'monospace' },
              }}
            />
            {logQuery && (
              <Chip size="small" label={`${visibleLines.length} match${visibleLines.length === 1 ? '' : 'es'}`}
                sx={{ height: 20, fontSize: '0.66rem' }} />
            )}
            <ToggleButtonGroup
              size="small" exclusive value={streamFilter}
              onChange={(_, v) => v && setStreamFilter(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1, py: 0.25, fontSize: 11.5 } }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="stdout">stdout</ToggleButton>
              <ToggleButton value="stderr" sx={{ color: '#ef4444' }}>stderr</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flex: 1 }} />

            <TextField
              select size="small" label="Tail" value={tail}
              onChange={(e) => setTail(Number(e.target.value))}
              sx={{ width: 92 }}
            >
              {[200, 500, 1000, 2000].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
            <Tooltip title={live ? 'Live tail on — click to pause' : 'Paused — click to resume live tail'}>
              <ToggleButton
                value="live" selected={live} size="small" onChange={() => setLive((v) => !v)}
                sx={{ px: 1.25, textTransform: 'none', fontWeight: 700, fontSize: 12,
                      color: live ? '#16a34a' : 'text.secondary',
                      borderColor: live ? 'rgba(34,197,94,0.5)' : undefined }}
              >
                <Box component="span" sx={{
                  width: 8, height: 8, borderRadius: '50%', mr: 0.75,
                  bgcolor: live ? '#22c55e' : 'text.disabled',
                  animation: live ? 'clLivePulse 1.4s ease-in-out infinite' : 'none',
                  '@keyframes clLivePulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                }} />
                {live ? 'LIVE' : 'PAUSED'}
                {live ? <PauseIcon fontSize="small" sx={{ ml: 0.5 }} /> : <PlayIcon fontSize="small" sx={{ ml: 0.5 }} />}
              </ToggleButton>
            </Tooltip>
            <Tooltip title={wrap ? 'Wrap on — click for no wrap' : 'No wrap — click to wrap lines'}>
              <IconButton size="small" onClick={() => setWrap((v) => !v)} color={wrap ? 'primary' : 'default'}><WrapIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Refresh"><IconButton size="small" onClick={() => fetchLogs(selectedId, false)}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Jump to bottom"><IconButton size="small" onClick={jumpToBottom}><ScrollIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Copy visible logs"><IconButton size="small" onClick={copyAll}><CopyIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Download visible logs"><IconButton size="small" onClick={downloadLogs}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Clear view"><IconButton size="small" onClick={() => setLines([])}><ClearLogIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>

          {/* Log body */}
          <Box
            ref={scrollRef}
            onScroll={onScroll}
            sx={{
              flex: 1, minHeight: 0, overflow: 'auto',
              bgcolor: dark ? '#0b0e14' : '#0f172a',
              color: '#d7dee8', fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.5, p: 1.5,
            }}
          >
            {loadingLogs ? (
              <Box sx={{ textAlign: 'center', pt: 4 }}><CircularProgress size={22} sx={{ color: '#94a3b8' }} /></Box>
            ) : error ? (
              <Typography sx={{ color: '#f87171', fontFamily: 'monospace', fontSize: 12.5 }}>{error}</Typography>
            ) : visibleLines.length === 0 ? (
              <Typography sx={{ color: '#64748b', fontFamily: 'monospace', fontSize: 12.5 }}>
                {lines.length === 0 ? 'No log output.' : 'No lines match the current filter.'}
              </Typography>
            ) : (
              visibleLines.map((l, i) => {
                const lvl = l.stream === 'stderr' ? 'error' : levelOf(l.message);
                return (
                  <Box key={i} sx={{
                    display: 'flex', gap: 1.25,
                    whiteSpace: wrap ? 'pre-wrap' : 'pre', wordBreak: wrap ? 'break-word' : 'normal',
                    bgcolor: lvl === 'error' ? 'rgba(248,113,113,0.07)' : lvl === 'warn' ? 'rgba(251,191,36,0.06)' : 'transparent',
                  }}>
                    <span style={{ color: '#5b6573', flexShrink: 0, userSelect: 'none' }}>
                      {(l.ts || '').replace('T', ' ').replace(/\.\d+Z?$/, '')}
                    </span>
                    <span style={{ color: LEVEL_COLOR[lvl] || '#d7dee8', flex: 1 }}>
                      {highlight(l.message || '', logQuery.trim())}
                    </span>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Status bar */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{
            px: 1.5, py: 0.5, borderTop: 1, borderColor: 'divider',
            fontSize: 11, color: 'text.secondary', bgcolor: dark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)',
          }}>
            <span>{logQuery || streamFilter !== 'all' ? `${visibleLines.length} of ${lines.length}` : `${lines.length}`} lines</span>
            {selected && <span>· node <b style={{ fontFamily: 'monospace' }}>{selected.node || 'node'}</b></span>}
            <Box sx={{ flex: 1 }} />
            <span>{atBottomRef.current ? 'autoscroll on' : 'scrolled up'}</span>
            {updatedAt && <span>· updated <RelTime at={updatedAt} /></span>}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

// Small relative-time label that ticks every few seconds.
const RelTime = ({ at }) => {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 2000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((Date.now() - at) / 1000));
  return <span>{secs < 2 ? 'just now' : secs < 60 ? `${secs}s ago` : `${Math.round(secs / 60)}m ago`}</span>;
};

const PageTitle = ({ stats, onRefresh, loading }) => (
  <Stack direction="row" alignItems="center" spacing={1.5}>
    <TerminalIcon color="primary" />
    <Box sx={{ flex: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Container Logs</Typography>
      <Typography variant="caption" color="text.secondary">
        Live tail across the cluster
        {stats ? ` — ${stats.running}/${stats.total} containers running on ${stats.nodes} node${stats.nodes === 1 ? '' : 's'}` : '.'}
      </Typography>
    </Box>
    {onRefresh && (
      <Tooltip title="Reload container list">
        <IconButton size="small" onClick={onRefresh} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
      </Tooltip>
    )}
  </Stack>
);

export default OperatorContainerLogsPage;
